import {
	ContractSuretyType,
	Dtos,
	formatToDollar,
	formatToPercent,
	type Rate,
	SuretyType,
} from "@inrev/common";
import { Option } from "@inrev/core";
import type { AdminBondRequest } from "@portal/api/bond-request/admin/types";
import { MetricCard } from "@portal/components/patterns/metric-card";
import { ClickableIcon, Text } from "@portal/components/primitives";
import { PortaledTooltip } from "@portal/components/ui/Tooltip";
import { type FC, useEffect, useId, useState } from "react";
import { formatEmpty } from "../../../../../utils";
import { ViewSectionLayout } from "../../../../shared/layouts/ViewSectionLayout";
import { AdminChangeBondRequestTermsModal } from "../../../shared/AdminChangeBondRequestTermsModal";
import {
	bondFormUnavailableMessage,
	isBondFormAvailable,
} from "../../../shared/bondFormAvailability";
import { canChangeTerms } from "../types";

export type AdminBondRequestTermsSummaryProps = {
	request: AdminBondRequest;
	bondForm: Dtos.Admin.SuretyBondForm.Get.Response | undefined;
	bondFormIsFetching?: boolean;
	bondFormError?: Error | null;
	accountRate: Rate | undefined;
};

const TERMS_GRID_CLASS = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4";

export const AdminBondRequestTermsSummary: FC<AdminBondRequestTermsSummaryProps> = ({
	request,
	bondForm,
	bondFormIsFetching = false,
	bondFormError = null,
	accountRate,
}) => {
	const [isChangeTermsModalOpen, setIsChangeTermsModalOpen] = useState(false);
	const editTooltipId = useId();
	const bondFormAvailable = isBondFormAvailable(bondForm, bondFormError);
	const editUnavailableMessage = bondFormUnavailableMessage(
		bondFormIsFetching,
		bondFormError,
		"changing terms",
	);

	useEffect(() => {
		if (!bondFormAvailable && isChangeTermsModalOpen) {
			setIsChangeTermsModalOpen(false);
		}
	}, [bondFormAvailable, isChangeTermsModalOpen]);

	if (request.suretyType !== SuretyType.contract) return null;

	const termsValues =
		request.contractSuretyType === ContractSuretyType.bid
			? [
					request.finalBondContingentRate,
					request.finalBondContingentPremium,
					request.agencyCommissionPercentage,
				]
			: [request.rate, request.premium, request.agencyCommissionPercentage];

	const hasTermsData = termsValues.every(Option.isSome);
	if (!hasTermsData) return null;

	const items =
		request.contractSuretyType === ContractSuretyType.bid
			? [
					{
						label: "Final Bond Rate",
						value: formatEmpty(formatToPercent(request.finalBondContingentRate)),
					},
					{
						label: "Final Bond Premium",
						value: formatEmpty(formatToDollar(request.finalBondContingentPremium, { round: true })),
					},
					{
						label: "Commission",
						value: formatEmpty(formatToPercent(request.agencyCommissionPercentage)),
					},
				]
			: [
					{ label: "Rate", value: formatEmpty(formatToPercent(request.rate)) },
					{
						label: "Premium",
						value: formatEmpty(formatToDollar(request.premium, { round: true })),
					},
					{
						label: "Commission",
						value: formatEmpty(formatToPercent(request.agencyCommissionPercentage)),
					},
				];

	const carrierName = request.carrier?.legalName;
	const canEditTerms = canChangeTerms(request);

	return (
		<>
			<ViewSectionLayout
				header={
					<span className="flex flex-wrap items-baseline gap-x-[10px] gap-y-[2px]">
						Terms
						{carrierName && (
							<Text type="body" color="secondary" data-testid="admin-bond-request-terms-carrier">
								{carrierName}
							</Text>
						)}
					</span>
				}
				partial={false}
				partialActive={false}
				button={
					canEditTerms ? (
						<>
							<span
								className={
									!bondFormAvailable
										? "cursor-not-allowed [&_button]:pointer-events-none"
										: undefined
								}
								{...(!bondFormAvailable
									? {
											tabIndex: 0,
											"aria-label": editUnavailableMessage,
											"data-tooltip-id": editTooltipId,
											"data-tooltip-content": editUnavailableMessage,
											"data-testid": "terms-edit-unavailable",
										}
									: {})}
							>
								<ClickableIcon
									type="pencil-square"
									size="mini"
									color="secondary"
									aria-label="Change terms"
									data-testid="admin-bond-request-terms-edit"
									trackingId="bond-request-admin-terms-edit"
									disabled={!bondFormAvailable}
									onClick={() => setIsChangeTermsModalOpen(true)}
								/>
							</span>
							{!bondFormAvailable && (
								<PortaledTooltip id={editTooltipId} style={{ maxWidth: 260 }} />
							)}
						</>
					) : undefined
				}
			>
				<div className={TERMS_GRID_CLASS} data-testid="admin-bond-request-terms-summary">
					{items.map(({ label, value }) => (
						<MetricCard key={label}>
							<MetricCard.Label size="md" className="whitespace-nowrap">
								{label}
							</MetricCard.Label>
							<MetricCard.Value size="md" className="whitespace-nowrap">
								{value}
							</MetricCard.Value>
						</MetricCard>
					))}
				</div>
			</ViewSectionLayout>

			{isChangeTermsModalOpen && canEditTerms && bondFormAvailable && (
				<AdminChangeBondRequestTermsModal
					request={request}
					bondForm={bondForm}
					accountRate={accountRate}
					onClose={() => setIsChangeTermsModalOpen(false)}
				/>
			)}
		</>
	);
};
